import { useState, useEffect } from "react";
import {
  Wallet,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Timer,
  X,
  History,
  Calendar,
  Building2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  useCarteiras,
  useActiveResgateToken,
  useGenerateResgateToken,
  useCancelResgateToken,
  useResgates,
} from "@/hooks/useResgates";
import { DataPagination } from "@/components/shared/DataPagination";
import type { Carteira, ResgateTipo, ResgateTokenComCode } from "@/types/api";

function fmtMoney(n: string | number) {
  return Number(n).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtCodigo(code: string) {
  return `${code.slice(0, 3)} ${code.slice(3)}`;
}

function Countdown({ expiresAt }: { expiresAt: string }) {
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    const update = () => {
      const diff = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setRemaining(diff);
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const display = `${m}:${String(s).padStart(2, "0")}`;

  return (
    <span className={remaining < 60 ? "text-destructive font-semibold" : "text-muted-foreground"}>
      {display}
    </span>
  );
}

type Step = "carteira" | "select" | "amount" | "otp" | "success";

export default function Resgates() {
  const [step, setStep] = useState<Step>("carteira");
  const [selectedCarteira, setSelectedCarteira] = useState<Carteira | null>(null);
  const [tipo, setTipo] = useState<ResgateTipo>("reais");
  const [valorStr, setValorStr] = useState("");
  const [activeToken, setActiveToken] = useState<ResgateTokenComCode | null>(null);

  const { data: carteiras, isLoading: loadingCarteiras } = useCarteiras();
  const { data: activeTokenReais } = useActiveResgateToken(selectedCarteira?.id ?? "", "reais");
  const { data: activeTokenPontos } = useActiveResgateToken(selectedCarteira?.id ?? "", "pontos");
  const generateToken = useGenerateResgateToken();
  const cancelToken = useCancelResgateToken();
  const [historyPage, setHistoryPage] = useState(1);
  const { data: resgatesData } = useResgates(historyPage);
  const resgates = resgatesData?.items ?? [];

  const saldoReais  = selectedCarteira?.saldo_reais  ?? "0.00";
  const saldoPontos = selectedCarteira?.saldo_pontos ?? "0";

  function handleSelectCarteira(c: Carteira) {
    setSelectedCarteira(c);
    setStep("select");
  }

  function handleSelectTipo(t: ResgateTipo) {
    setTipo(t);
    setStep("amount");
  }

  function handleGenerate() {
    if (!selectedCarteira) return;
    const valor = valorStr.replace(",", ".");
    if (!valor || parseFloat(valor) <= 0) {
      toast.error("Informe um valor válido.");
      return;
    }

    const saldoDisp = tipo === "reais" ? parseFloat(saldoReais) : parseFloat(saldoPontos);
    if (parseFloat(valor) > saldoDisp) {
      const label = tipo === "reais" ? `R$ ${fmtMoney(saldoDisp)}` : `${Math.round(saldoDisp)} pts`;
      toast.error(`Saldo insuficiente. Disponível: ${label}`);
      return;
    }

    generateToken.mutate(
      { valor, tipo, carteira_id: selectedCarteira.id },
      {
        onSuccess: (token) => {
          setActiveToken(token);
          setStep("otp");
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
            "Erro ao gerar código.";
          toast.error(msg);
        },
      }
    );
  }

  function handleCancel() {
    if (!activeToken) return;
    cancelToken.mutate(activeToken.id, {
      onSuccess: () => {
        setActiveToken(null);
        setStep("select");
        setValorStr("");
        toast.info("Código cancelado.");
      },
    });
  }

  function handleReset() {
    setStep("carteira");
    setSelectedCarteira(null);
    setValorStr("");
    setActiveToken(null);
  }

  if (loadingCarteiras) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-md mx-auto">
      <h1 className="text-2xl font-bold text-foreground">Resgates</h1>

      {/* Step: selecionar carteira */}
      {step === "carteira" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Selecionar Empresa
            </CardTitle>
            <CardDescription>Escolha de qual empresa deseja resgatar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {!carteiras || carteiras.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground py-4">
                Nenhuma carteira disponível. Aceite um convite de empresa para começar.
              </p>
            ) : (
              carteiras.map((c) => (
                <Button
                  key={c.id}
                  className="w-full justify-between"
                  variant="outline"
                  onClick={() => handleSelectCarteira(c)}
                >
                  <span className="font-medium">{c.company_name}</span>
                  <span className="text-xs text-muted-foreground space-x-2">
                    <span>R$ {fmtMoney(c.saldo_reais)}</span>
                    <span>·</span>
                    <span>{c.saldo_pontos} pts</span>
                  </span>
                </Button>
              ))
            )}
          </CardContent>
        </Card>
      )}

      {/* Saldo da carteira selecionada */}
      {step !== "carteira" && selectedCarteira && (
        <>
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Building2 className="h-4 w-4" />
              {selectedCarteira.company_name}
            </p>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-xs">
              Trocar empresa
            </Button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs uppercase font-semibold">Saldo R$</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-primary" />
                  R$ {fmtMoney(saldoReais)}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription className="text-xs uppercase font-semibold">Saldo Pts</CardDescription>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-primary" />
                  {saldoPontos} pts
                </CardTitle>
              </CardHeader>
            </Card>
          </div>
        </>
      )}

      {/* Active tokens notice */}
      {(activeTokenReais || activeTokenPontos) && step === "select" && (
        <Card className="border-yellow-500/40 bg-yellow-50/30">
          <CardContent className="pt-4 text-sm text-muted-foreground">
            <p>Você tem código(s) ativos. Gerar um novo código do mesmo tipo cancela o anterior.</p>
          </CardContent>
        </Card>
      )}

      {/* Step: select tipo */}
      {step === "select" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Solicitar Resgate</CardTitle>
            <CardDescription>Escolha o tipo de saldo a resgatar.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button
              className="w-full justify-between"
              variant="outline"
              onClick={() => handleSelectTipo("reais")}
              disabled={parseFloat(saldoReais) <= 0}
            >
              <span>Resgatar R$</span>
              <span className="font-semibold">R$ {fmtMoney(saldoReais)}</span>
            </Button>
            {selectedCarteira?.shop_handle ? (
              <Button className="w-full justify-between" variant="outline" asChild>
                <a
                  href={`https://shop.indiqr.com.br/${selectedCarteira.shop_handle}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span>Abrir Loja</span>
                  <span className="font-semibold">{saldoPontos} pts</span>
                </a>
              </Button>
            ) : (
              <Button
                className="w-full justify-between"
                variant="outline"
                onClick={() => handleSelectTipo("pontos")}
                disabled={parseFloat(saldoPontos) <= 0}
              >
                <span>Resgatar Pontos</span>
                <span className="font-semibold">{saldoPontos} pts</span>
              </Button>
            )}
            {parseFloat(saldoReais) <= 0 && parseFloat(saldoPontos) <= 0 && !selectedCarteira?.shop_handle && (
              <p className="text-center text-sm text-muted-foreground py-2">
                Nenhum saldo disponível para resgate nesta empresa.
              </p>
            )}
            <Button variant="ghost" size="sm" className="w-full" onClick={() => setStep("carteira")}>
              <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step: amount */}
      {step === "amount" && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Valor do Resgate</CardTitle>
            <CardDescription>
              {tipo === "reais"
                ? `Disponível: R$ ${fmtMoney(saldoReais)}`
                : `Disponível: ${saldoPontos} pts`}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="valor">
                {tipo === "reais" ? "Valor em R$" : "Quantidade de pontos"}
              </Label>
              <Input
                id="valor"
                type="number"
                placeholder={tipo === "reais" ? "0,00" : "0"}
                min="0.01"
                step={tipo === "reais" ? "0.01" : "1"}
                value={valorStr}
                onChange={(e) => setValorStr(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 gap-2" onClick={() => setStep("select")}>
                <ArrowLeft className="h-4 w-4" />
                Voltar
              </Button>
              <Button
                className="flex-1 gap-2"
                onClick={handleGenerate}
                disabled={generateToken.isPending}
              >
                {generateToken.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    Gerar Código
                    <ArrowRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step: OTP display */}
      {step === "otp" && activeToken && (
        <Card className="border-primary/40">
          <CardHeader className="pb-2">
            <CardDescription className="text-xs uppercase font-semibold tracking-wider">
              Código de Resgate
            </CardDescription>
            <CardTitle className="text-4xl tracking-[0.25em] text-center py-4 font-mono">
              {fmtCodigo(activeToken.code)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between text-sm rounded-lg border bg-muted/30 p-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Timer className="h-4 w-4" />
                Válido por
              </div>
              <Countdown expiresAt={activeToken.expires_at} />
            </div>
            <div className="text-center text-sm text-muted-foreground">
              Valor:{" "}
              <span className="font-semibold">
                {tipo === "reais"
                  ? `R$ ${fmtMoney(activeToken.valor)}`
                  : `${Math.round(parseFloat(activeToken.valor))} pts`}
              </span>
            </div>
            <p className="text-center text-sm text-muted-foreground">
              Mostre este código ao vendedor para receber seu pagamento.
            </p>
            <Separator />
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-2 text-destructive hover:text-destructive"
              onClick={handleCancel}
              disabled={cancelToken.isPending}
            >
              {cancelToken.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <X className="h-4 w-4" />
              )}
              Cancelar código
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Histórico de Resgates */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" />
            Histórico
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!resgates || resgates.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Nenhum resgate confirmado ainda.
            </p>
          ) : (
            <>
              <div className="space-y-3">
                {resgates.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                  >
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">
                        {r.tipo === "reais"
                          ? `R$ ${fmtMoney(r.valor)}`
                          : `${Math.round(parseFloat(r.valor))} pts`}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(r.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>
                    <Check className="h-4 w-4 text-green-600" />
                  </div>
                ))}
              </div>
              <div className="mt-4">
                <DataPagination
                  page={resgatesData?.page ?? 1}
                  pages={resgatesData?.pages ?? 1}
                  onPageChange={setHistoryPage}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
